<template>
  <v-dialog
    :value="value"
    max-width="900px"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card class="rounded-lg">
      <v-card-title class="title primary--text text--darken-3 px-4 py-2">
        {{ title }}
        <v-spacer></v-spacer>
        <v-chip label color="primary" class="white--text title mr-2">
          {{ selectedIds.length }}
        </v-chip>
        <span class="black--text text-body-1">
          of {{ variables.length }} selected
        </span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text style="padding: 0px 16px 16px 16px;">
        <v-text-field
          v-model="search"
          label="Show variables with a domain or name that matches this text:"
          prepend-inner-icon="mdi-magnify"
          clearable
          class="ma-0 pa-0 pt-4"
          hide-details
        ></v-text-field>

        <v-data-table
          :items="variables"
          :headers="headers"
          :search="search"
          :custom-filter="customFilter"
          item-key="uid"
          dense
          disable-pagination
          hide-default-footer
          class="mt-3"
          @current-items="setShownItems"
        >
          <template v-slot:header.selected>
            <v-tooltip top color="primary">
              <template v-slot:activator="{ on: tooltip }">
                <span v-on="{ ...tooltip }">
                  <v-simple-checkbox
                    :value="allShownSelected"
                    :indeterminate="someShownSelected"
                    hide-details
                    dense
                    @input="toggleAllShown"
                  />
                </span>
              </template>
              <span>Select or clear all {{ shownItems.length }} shown</span>
            </v-tooltip>
          </template>

          <template v-slot:item="props">
            <tr>
              <td style="width: 5%;">
                <v-simple-checkbox
                  :value="isSelected(props.item)"
                  hide-details
                  dense
                  @input="toggle(props.item)"
                />
              </td>
              <td class="text-subtitle-1" style="width: 22%;">
                <v-row align="center" class="ma-0 pa-0 flex-nowrap">
                  <img
                    :src="'/images/' + props.item.category + '-icon-128.png'"
                    :title="props.item.category"
                    style="height: 2em;"
                    class="ma-1"
                    @error="missingDomainFallback"
                  />
                  <span class="ml-1">{{ props.item.category }}</span>
                </v-row>
              </td>
              <td class="text-subtitle-1" style="width: 28%;">
                <v-tooltip top color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <span v-on="{ ...tooltip }">
                      {{
                        useLongScaleNames
                          ? props.item.label
                          : props.item.abbreviation
                      }}
                    </span>
                  </template>
                  <span>
                    {{
                      useLongScaleNames
                        ? props.item.description
                        : props.item.label
                    }}
                  </span>
                </v-tooltip>
              </td>
              <td class="text-subtitle-1" style="width: 12%;">
                {{ props.item.data_category }}
              </td>
              <td
                class="text-subtitle-1 text-wrap"
                style="width: 33%; line-height: 1.25em;"
              >
                {{ props.item.description }}
              </td>
            </tr>
          </template>

          <template v-slot:no-data>
            No variables available.
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-btn text color="primary" @click="clearAll">
          <v-icon left>mdi-close-circle-outline</v-icon>Clear all
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="$emit('input', false)">
          <v-icon left dark>close</v-icon>Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/datasetSummary/types';
import { missingDomainFallback } from '@/utils/helpers';

export default {
  name: 'VariablePickerDialog',
  props: {
    // dialog open state (v-model)
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    // variables offered here, each already carrying a `uid` (see variableKey)
    variables: {
      type: Array,
      required: true,
    },
    // selected uids; updated via the .sync modifier
    selectedIds: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      search: '',
      shownItems: [],
      missingDomainFallback: missingDomainFallback,
      headers: [
        {
          text: '',
          value: 'selected',
          sortable: false,
          filterable: false,
          width: '5%',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Domain',
          value: 'category',
          sortable: true,
          filterable: true,
          width: '22%',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Variable',
          value: 'abbreviation',
          sortable: true,
          filterable: true,
          width: '28%',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Type',
          value: 'data_category',
          sortable: true,
          filterable: true,
          width: '12%',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Description',
          value: 'description',
          sortable: false,
          filterable: true,
          width: '33%',
          class: 'text-subtitle-1 font-weight-bold',
        },
      ],
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
    }),
    selectedSet() {
      return new Set(this.selectedIds);
    },
    shownSelectedCount() {
      return this.shownItems.filter(v => this.selectedSet.has(v.uid)).length;
    },
    allShownSelected() {
      return (
        this.shownItems.length > 0 &&
        this.shownSelectedCount === this.shownItems.length
      );
    },
    someShownSelected() {
      return (
        this.shownSelectedCount > 0 &&
        this.shownSelectedCount < this.shownItems.length
      );
    },
  },
  methods: {
    setShownItems(items) {
      this.shownItems = items;
    },
    isSelected(v) {
      return this.selectedSet.has(v.uid);
    },
    emitSelection(ids) {
      this.$emit('update:selectedIds', ids);
    },
    toggle(v) {
      if (this.selectedSet.has(v.uid)) {
        this.emitSelection(this.selectedIds.filter(id => id !== v.uid));
      } else {
        this.emitSelection([...this.selectedIds, v.uid]);
      }
    },
    // acts on the filtered rows only, leaving hidden selections untouched
    toggleAllShown() {
      const shown = this.shownItems.map(v => v.uid);
      if (this.allShownSelected) {
        const drop = new Set(shown);
        this.emitSelection(this.selectedIds.filter(id => !drop.has(id)));
      } else {
        this.emitSelection(
          Array.from(new Set([...this.selectedIds, ...shown]))
        );
      }
    },
    clearAll() {
      this.emitSelection([]);
    },
    // matches the filter used by the Variables tab
    customFilter(value, search, item) {
      const lcSearch = search == null ? null : search.toLowerCase();
      return (
        lcSearch == null ||
        item.category.toLowerCase().indexOf(lcSearch) !== -1 ||
        item.abbreviation.toLowerCase().indexOf(lcSearch) !== -1 ||
        item.label.toLowerCase().indexOf(lcSearch) !== -1
      );
    },
  },
};
</script>

<style scoped></style>
